import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { renderGraphicToCanvas } from "./downloadGraphic";
import { LAYOUT } from "./layout";
import { GraphicCustomization } from "./types";

/**
 * Live preview of the graphic. Uses the same canvas + OpenType path pipeline
 * as PNG export so Safari, Chrome, and download share identical glyph geometry
 * (browser fillText/DOM metrics still differ across engines).
 */

interface StampPreviewProps {
  customization: GraphicCustomization;
  /** Used for initial seeding context only; stamp text comes from customization */
  firstName: string;
}

const StampPreview: React.FC<StampPreviewProps> = ({
  customization,
  firstName,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [ready, setReady] = useState(false);
  const layout = LAYOUT[customization.orientation];
  const { width: cw, height: ch } = layout.canvas;

  useEffect(() => {
    let cancelled = false;
    setReady(false);

    (async () => {
      try {
        const rendered = await renderGraphicToCanvas(customization, firstName);
        if (cancelled || !canvasRef.current) return;
        const dest = canvasRef.current;
        const ctx = dest.getContext("2d");
        if (!ctx) return;
        if (dest.width !== rendered.width || dest.height !== rendered.height) {
          dest.width = rendered.width;
          dest.height = rendered.height;
        }
        ctx.clearRect(0, 0, dest.width, dest.height);
        ctx.drawImage(rendered, 0, 0);
        setReady(true);
      } catch {
        if (!cancelled) setReady(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [customization, firstName]);

  return (
    <Frame ratio={cw / ch}>
      <PreviewCanvas
        ref={canvasRef}
        width={cw}
        height={ch}
        aria-label="Share graphic preview"
        $ready={ready}
      />
    </Frame>
  );
};

const Frame = styled.div<{ ratio: number }>`
  position: relative;
  width: 100%;
  aspect-ratio: ${({ ratio }) => ratio};
  overflow: hidden;
  border-radius: 8px;
  background: #f5eedd;
`;

const PreviewCanvas = styled.canvas<{ $ready: boolean }>`
  display: block;
  width: 100%;
  height: 100%;
  opacity: ${({ $ready }) => ($ready ? 1 : 0.55)};
  transition: opacity 120ms ease;
`;

export default StampPreview;
